import { AxiosResponse } from "axios";
import rfdc from "rfdc";
import { connectCollabEventStream, ICollabStreamHandle } from "./collab-events-stream";
import { isCollabEditingEnabled } from "./collab-feature-gate";
import { collabIdsEqual } from "./collab-id";
import { CollabService } from "./collab-service";
import {
  ICollabCursor,
  ICollabJoinResponse,
  ICollabOpResult,
  ICollabOpsResponse,
  ICollabParticipant,
  ICollabReplayResponse,
  ICollabStateResponse,
  ICollabStreamEvent
} from "./collab-types";

const cloneDoc = rfdc();

/** Per-tab identity for the collab session — stable across reconnects, unique per tab. */
export const COLLAB_CLIENT_ID: string =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tab-${Math.floor(performance.now())}-${Math.floor(Math.random() * 1e9)}`;

const OP_FLUSH_THROTTLE_MS: number = 150;
const SELECTION_THROTTLE_MS: number = 150;
const COLLAB_UNDO_STACK_LIMIT: number = 50;
/** Transient send failures back off exponentially from OP_FLUSH_THROTTLE_MS up to this cap. */
const OP_FLUSH_BACKOFF_MAX_MS: number = 5000;
/**
 * Hard client-side batch slice — the server rejects batches over MaxOpsPerBatch=200
 * (CollabSession.cs, BAD_BATCH) as a whole, so we send bounded chunks with headroom
 * and let the flush loop drain the tail. Legitimate bulk edits (theme apply, big
 * paste) converge over several requests instead of failing outright.
 */
const COLLAB_MAX_OPS_PER_BATCH: number = 180;
/** A single-gesture diff above this op count almost always means shadow/live drift — telemetry only. */
const LARGE_DIFF_BREADCRUMB_THRESHOLD: number = 50;

let opCounter: number = 0;

export function makeCollabOpId(): string {
  opCounter += 1;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${COLLAB_CLIENT_ID}-${opCounter}`;
}

/** One local gesture: the ops it produced and their pre-computed inverses (Figma-style per-user undo). */
export interface ICollabUndoEntry<TOp> {
  forwardOps: TOp[];
  inverseOps: TOp[];
}

/** The one in-flight join attempt — overlapping join() calls for the same entity share it. */
interface ICollabJoinInFlight {
  entityId: string;
  promise: Promise<void>;
}

/** Server-side id-collision remap returned in an ack — old id → new id. */
export interface ICollabIdRemap {
  [oldId: string]: string;
}

/** Why the core mutated the live doc: a remote/correction op batch vs a full authoritative resync. */
export type CollabRemoteChangeKind = "op" | "resync";

/**
 * Everything the entity-agnostic session core needs to know about one collab domain
 * (screen tree today, workflow diagram next). Implementations wrap the domain's
 * diff/apply/invert engine (sd-patch for screens) and its live-document accessors.
 */
export interface ICollabDomainAdapter<TDoc, TOp> {
  /**
   * The live document as a serializable snapshot (join seeding + local diffing), or null
   * when the document is not ready (e.g. no identity yet) — the core then skips the step.
   */
  getSerializableDoc(): TDoc | null;
  /** The MUTABLE live document that remote ops, ack corrections, and id remaps apply onto. */
  getLiveDoc(): TDoc;
  /**
   * The identity of a doc (screen id / workflow id) as a canonical string; null when the
   * doc carries none. Powers the cross-entity guards (KOA-18173): a join seed whose doc id
   * differs from the session entity is refused, and emitLocalOps never diffs docs whose
   * identities disagree with each other or with the session — a stale session must never
   * receive another entity's content.
   */
  getDocId?(doc: TDoc): string | null;
  /** Order-aware diff of the shadow doc against the live doc → granular ops (fresh opIds). */
  diff(before: TDoc, after: TDoc): TOp[];
  /** Pre-computed inverses of `ops` against the PRE-mutation doc (per-user undo). */
  invert(before: TDoc, ops: TOp[]): TOp[];
  /** Applies ops onto a doc (shadow or live) in place; returns the ops that could NOT apply. */
  applyOps(doc: TDoc, ops: TOp[]): TOp[];
  /** Commits/renders the live doc after the core mutated it (dispatch back into the app). */
  commitRemoteDoc(doc: TDoc, kind: CollabRemoteChangeKind): void;
  /** Parses an authoritative snapshot; null when structurally invalid (core ignores it). */
  parseSnapshot(json: string): TDoc | null;
  /** The LWW conflict key of an op (convergence rule 1); null when the op is not LWW-mergeable. */
  lwwKeyOf(op: TOp): string | null;
  /** Structural ops must apply in global seq order on every replica (convergence rule 2). */
  isStructural(op: TOp): boolean;
  /** Batch-level merge before send (e.g. successive propSets on one key keep only the last). */
  coalesce(ops: TOp[]): TOp[];
  /** Fresh opId, no server-resolved position — undo/redo replays ops as a brand-new gesture. */
  remintForReplay(op: TOp): TOp;
  /**
   * Author-side correction moving own structural op to the server-resolved position
   * (convergence rule 3); null when the op kind has no position to correct.
   */
  makeResolvedIndexCorrection(op: TOp, resolvedIndex: number): TOp | null;
  /**
   * Optional: the structural op's subject node index within its target parent in `doc` —
   * read right after the op applied, i.e. the client's OPTIMISTIC placement. When an
   * ack's resolvedIndex equals it, the server agreed with this client and the correction
   * is skipped: applying it anyway rebases a stale position onto a tree that LATER local
   * gestures may have already changed, transiently reverting them (KOA-18198 flicker on
   * rapid item-tree moves), and forces a redundant full re-render on every gesture even
   * when nothing moved. null = no skip (conservative). Absent for workflow: wf ops carry
   * no resolvedIndex, so there is nothing to compare.
   */
  optimisticIndexOf?(doc: TDoc, op: TOp): number | null;
  /** True when a SKIPPED remote op leaves this replica divergent (→ resync from authority). */
  skippedOpForcesResync(op: TOp): boolean;
  /**
   * Optional domain-specific CONSERVATIVE conflict rule, evaluated on every remote op BEFORE
   * the structural/LWW handling: true → this replica resyncs from the authority instead of
   * applying the batch. Workflow wires `wfRemoteOpForcesResync` (element-scope upsert
   * conflicts; remote structural op while own structural ops are unacked — wf ships no
   * resolvedIndex corrections, so the sound recovery is the authoritative snapshot).
   * Absent for screens: never fires, sd-patch keeps its resolvedIndex healing.
   */
  remoteOpForcesResync?(
    remoteOp: TOp,
    remoteSeq: number,
    pendingOps: readonly TOp[],
    ackedKeySeq: ReadonlyMap<string, number>
  ): boolean;
  /**
   * Optional LWW key CONFLICT comparison used by the in-flight and acked-seq suppression
   * rules. Workflow wires `wfLwwKeysConflict` (ancestor/descendant prop-path prefixes
   * conflict); when a remote key merely PREFIX-conflicts an acked key the comparison is
   * between different keys, so the remote op applies only if it out-sequences EVERY
   * conflicting acked key. Absent for screens: exact key equality (unchanged semantics).
   */
  lwwKeysConflict?(a: string, b: string): boolean;
  /**
   * Optional server stamps carried on an ACCEPTED own-op result that the origin must adopt
   * (workflow: wfElementRemove `cascadedIds` — the hub suppresses the origin's broadcast
   * echo, so the ack is the only channel the authoritative cascade closure reaches it on).
   * Returns the ops to re-apply onto shadow + live; [] when the result carries nothing.
   * Absent for screens: results never carry adoption stamps.
   */
  makeAckAdoptionOps?(op: TOp, result: ICollabOpResult): TOp[];
  /** Server remapped colliding ids inside one of our adds — re-key a doc in place. */
  remapIds(doc: TDoc, idRemap: ICollabIdRemap): void;
  /**
   * Optional wire seed for join. When present, its result travels as `initialJson` instead
   * of JSON.stringify(getSerializableDoc()) — for domains whose join payload is NOT the
   * diffable doc (workflow: the {"xml","json"} envelope). null skips the join entirely.
   */
  buildJoinSeedJson?(): Promise<string | null>;
}

/**
 * The provider-side surface the core reports into: React state (session flags, roster)
 * and the domain's presence sources/sinks stay in the owning provider partition.
 */
export interface ICollabSessionHost {
  /** Session established — the host mirrors active/writer flags and the initial roster. */
  onSessionJoined(roster: ICollabParticipant[]): void;
  /** Session torn down — the host clears its mirrored session state (guarding its own no-op case). */
  onSessionLeft(): void;
  /** Roster (or one participant's selection) changed — the host stores it and fans out remote selections. */
  applyRosterUpdate(roster: ICollabParticipant[]): void;
  /** Current roster as the host stores it (selection events patch one participant into it). */
  getRoster(): ICollabParticipant[];
  /** The local selection to broadcast as presence. */
  getSelectedIds(): string[];
  /**
   * The server refused our join seed (workflow envelope parse failure) — the host degrades
   * to legacy single-user editing (one info toast, no session). Never fires for screens.
   */
  onSeedRejected?(reason: string): void;
}

export interface ICollabSessionCoreOptions<TDoc, TOp> {
  /** Entity segment of the collab REST/SSE routes: /collab/{entityRoute}/{id}/… ("screen"). */
  entityRoute: string;
  adapter: ICollabDomainAdapter<TDoc, TOp>;
  host: ICollabSessionHost;
}

/**
 * Entity-agnostic core of this tab's membership in a real-time collaboration session —
 * P2: FULL CONCURRENT EDITING. Every participant edits; local mutations are converted
 * into granular ops by diffing the live doc against a core-owned SHADOW DOC (the last
 * state both sides agreed on), sent to the server (single total order = LWW), and
 * everyone else's accepted ops stream back over SSE and apply onto both the live and
 * shadow docs. Domain specifics (diff/apply/invert, op semantics, rendering) come in
 * through an ICollabDomainAdapter; React state stays in the owning provider partition
 * behind ICollabSessionHost.
 *
 * Invariants:
 * - shadowDoc always equals "live doc minus my unsent local edits" — local diffs are
 *   computed against it, remote ops are applied to it, so a remote batch arriving between
 *   two local gestures can never be misread as a local change.
 * - Only server-accepted ops mutate other clients; a reject (or seq gap) triggers a full
 *   resync from the authoritative state rather than any local repair heuristics.
 */
export class CollabSessionCore<TDoc, TOp> {
  private readonly adapter: ICollabDomainAdapter<TDoc, TOp>;
  private readonly host: ICollabSessionHost;
  private readonly entityRoute: string;

  private sessionEntityId: string | null = null;
  private joinInFlight: ICollabJoinInFlight | null = null;
  private leaveToken: string | null = null;
  /**
   * Monotonic join generation. Every join() and leave() advances it; a join whose epoch
   * moved during one of its awaits abandons its result. Without this, two rapid entity
   * switches leave two joins in flight and the LAST RESPONSE (not the last intent) wins —
   * pinning the session to an entity the user already left (KOA-18173 race).
   */
  private joinEpoch: number = 0;
  private streamHandle: ICollabStreamHandle | null = null;
  private lastSeq: number = 0;
  private applyingRemoteChange: boolean = false;
  private resyncInFlight: boolean = false;

  private shadowDoc: TDoc | null = null;
  private outgoingOps: TOp[] = [];
  private inFlightOps: TOp[] = [];
  private opFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private opSendInFlight: boolean = false;
  private consecutiveSendFailures: number = 0;
  private selectionTimer: ReturnType<typeof setTimeout> | null = null;
  private pagehideHandler: (() => void) | null = null;
  private localCursor: ICollabCursor | null = null;
  private undoEntries: ICollabUndoEntry<TOp>[] = [];
  private redoEntries: ICollabUndoEntry<TOp>[] = [];

  /** Highest server seq at which WE last wrote each contested key (seq-aware LWW). */
  private ackedKeySeq: Map<string, number> = new Map<string, number>();

  /**
   * Per pending structural op: where the local optimistic apply placed its node
   * (adapter.optimisticIndexOf, captured at emit). Entries are consumed by the op's ack
   * (any status) and cleared with the rest of the session state on leave/resync.
   */
  private optimisticIndexByOp: Map<TOp, number> = new Map<TOp, number>();

  /**
   * STRUCTURAL ORDER INVARIANT (proven by the sd-patch convergence simulation): node
   * add/remove/move must apply in global seq order on every replica — positions are
   * order-sensitive. An out-of-order structural op means this replica interleaved
   * adversely; recovery is a resync from the authority, never a local guess.
   */
  private lastStructuralSeq: number = 0;

  public constructor(options: ICollabSessionCoreOptions<TDoc, TOp>) {
    this.adapter = options.adapter;
    this.host = options.host;
    this.entityRoute = options.entityRoute;
  }

  public isSessionActive = (): boolean => {
    return this.sessionEntityId !== null;
  };

  public getSessionEntityId = (): string | null => {
    return this.sessionEntityId;
  };

  /** True while the core is committing a remote change — the host must not re-diff it as local. */
  public isApplyingRemoteChange = (): boolean => {
    return this.applyingRemoteChange;
  };

  // ---- lifecycle ------------------------------------------------------------------------

  public join = async (entityId: string): Promise<void> => {
    if (!isCollabEditingEnabled()) return;
    // SINGLE-FLIGHT: sessionEntityId is only set AFTER the join round-trip, so two
    // overlapping join() calls (setSelectedWf-commit + canvas-ready both attempt, by
    // design) would otherwise BOTH pass the guards — two REST joins, and the second
    // SSE handle overwrites the first without stopping it (leaked EventSource).
    const inFlight: ICollabJoinInFlight | null = this.joinInFlight;
    if (inFlight) {
      if (inFlight.entityId === entityId) return inFlight.promise;
      // Switching entities mid-join: let the previous attempt settle so its session
      // state is real, then re-evaluate every guard against the settled state.
      await inFlight.promise;
      return this.join(entityId);
    }
    if (this.sessionEntityId === entityId) return;
    if (this.sessionEntityId) void this.leave();
    // The epoch pins THIS attempt's generation: leave() (screen switch teardown, agent
    // suspend) advances it, and the in-flight attempt then abandons its response instead
    // of establishing a session for an entity the tab already left (KOA-18173).
    const epoch: number = ++this.joinEpoch;

    const attempt: Promise<void> = this.performJoin(entityId, epoch);
    this.joinInFlight = { entityId, promise: attempt };
    try {
      await attempt;
    } finally {
      this.joinInFlight = null;
    }
  };

  private performJoin = async (entityId: string, epoch: number): Promise<void> => {
    try {
      // Seed the session with the doc this tab just loaded (first joiner wins; the
      // server ignores the payload otherwise and returns its authoritative snapshot).
      const localDoc: TDoc | null = this.adapter.getSerializableDoc();
      if (!localDoc) {
        return;
      }
      // The seed must BE the entity we are joining. During an entity switch the live doc
      // can briefly belong to another screen/workflow — seeding the server with a foreign
      // tree would flush that tree into this entity's row (KOA-18173).
      const localDocId: string | null = this.adapter.getDocId ? this.adapter.getDocId(localDoc) : null;
      if (localDocId != null && !collabIdsEqual(localDocId, entityId)) {
        return;
      }
      let initialJson: string;
      if (this.adapter.buildJoinSeedJson) {
        const seedJson: string | null = await this.adapter.buildJoinSeedJson();
        if (epoch !== this.joinEpoch) return;
        if (!seedJson) {
          return;
        }
        initialJson = seedJson;
      } else {
        initialJson = JSON.stringify(localDoc);
      }
      // Freeze the first-joiner shadow at the SAME instant as the seed, BEFORE the join
      // round-trip. The live tree can be mutated during the await (component migration on
      // project open) — a shadow cloned after the response would silently diverge from
      // what the server was actually seeded with, and the divergence would never be
      // emitted. With the T0 clone, any in-flight delta surfaces as a normal local diff
      // on the next emit and the authority converges on its own.
      const seedShadow: TDoc = cloneDoc(localDoc);

      const res: AxiosResponse<ICollabJoinResponse> = await CollabService.Join(
        entityId,
        COLLAB_CLIENT_ID,
        initialJson,
        this.entityRoute
      );
      if (epoch !== this.joinEpoch) {
        // A newer join/leave superseded this one mid-flight. If the server DID establish a
        // session for us, release it so the roster carries no ghost participant.
        const staleToken: string | null = res?.data?.leaveToken ?? null;
        if (res && res.status === 200 && staleToken && !res.data.seedRejected) {
          void CollabService.Leave(entityId, COLLAB_CLIENT_ID, staleToken, this.entityRoute);
        }
        return;
      }
      if (!res || res.status !== 200 || !res.data) {
        return;
      }

      const join: ICollabJoinResponse = res.data;
      if (join.seedRejected) {
        // The server refused our seed (envelope parse failure) — no session was established
        // for us; stay sessionless and let the host degrade to legacy single-user editing.
        this.host.onSeedRejected?.(join.seedReason ?? "");
        return;
      }
      this.sessionEntityId = entityId;
      this.leaveToken = join.leaveToken;
      this.lastSeq = join.seq;
      this.outgoingOps = [];
      this.inFlightOps = [];
      this.host.onSessionJoined(join.roster ?? []);

      if (join.snapshotJson) {
        // The session was already live — its authoritative doc replaces our local copy.
        this.applyAuthoritativeSnapshot(join.snapshotJson);
      } else {
        this.shadowDoc = seedShadow;
      }

      this.streamHandle = connectCollabEventStream(
        entityId,
        COLLAB_CLIENT_ID,
        this.handleStreamEvent,
        this.recoverAfterReconnect,
        this.entityRoute,
        (status: number): void => {
          // The stream is the earliest detector: it re-hits the endpoint every 3s, so a
          // session lost to a restart or an expired participant is reclaimed long before
          // the user's next edit would have been thrown away.
          if (CollabSessionCore.isSessionGoneStatus(status)) {
            void this.handleSessionGone("stream");
          }
        }
      );

      this.pagehideHandler = () => {
        if (this.sessionEntityId && this.leaveToken) {
          CollabService.LeaveBeacon(this.sessionEntityId, COLLAB_CLIENT_ID, this.leaveToken, this.entityRoute);
        }
      };
      window.addEventListener("pagehide", this.pagehideHandler);
    } catch (error: unknown) {
      console.error("[collab] join failed:", error);
    }
  };

  /**
   * Entity-switch teardown: push whatever the user just did into THIS session, then leave —
   * ordered BEFORE the next entity's tree is committed, so no funnel can ever pair the old
   * session with the new entity's content (KOA-18173).
   */
  public flushAndLeave = async (): Promise<void> => {
    if (!this.sessionEntityId) return;
    await this.flushOps();
    await this.leave();
  };

  /**
   * The SERVER no longer knows this session — reclaim it instead of editing into a void.
   *
   * Sessions are process-local in-memory state on CoreApi: the janitor removes one once
   * its last participant's stream has been gone past the reconnect grace, and a restart
   * or deploy drops them all at once. Nothing told the client, so `sessionEntityId`
   * stayed set forever: op batches 404ed and were discarded, `/sync` 404ed, and the
   * screen save routed through a funnel that could not persist anything while reporting
   * no error — the reporter's components "flew away" over and over (KOA-18256, reproduced
   * on testrnmigration: DB kept 45 components while the canvas showed 46).
   *
   * Recovery drops the dead session FIRST — while `sessionEntityId` still points at it,
   * `join` short-circuits on the same entity and the save funnel keeps trusting it — then
   * re-joins. The re-join seeds the server from the LIVE doc, so everything the user did
   * during the outage becomes the authoritative tree instead of being lost. If the
   * re-join fails, the session stays inactive, which is what lets the save funnel fall
   * back to the legacy whole-screen PUT.
   */
  private sessionGoneRecoveryInFlight: boolean = false;

  private handleSessionGone = async (origin: string): Promise<void> => {
    const entityId: string | null = this.sessionEntityId;
    if (!entityId || this.sessionGoneRecoveryInFlight) return;
    this.sessionGoneRecoveryInFlight = true;
    try {
      this.leave();
      await this.join(entityId);
    } catch (error: unknown) {
      console.error("[collab] session recovery failed:", error);
    } finally {
      this.sessionGoneRecoveryInFlight = false;
    }
  };

  /**
   * A 404 means the session itself is gone; a 403 (NOT_PARTICIPANT) means it still exists
   * but no longer counts us as a member — the janitor expired our participant and has not
   * swept the session yet. Both are unrecoverable by resyncing (a snapshot cannot make us
   * a participant again); the only repair is re-joining.
   */
  private static isSessionGoneStatus(status: number | undefined): boolean {
    return status === 404 || status === 403;
  }

  /** Save funnel hook: `/sync` answered "session gone", so reclaim it out of band. */
  public reclaimAfterSessionLoss = (origin: string): void => {
    void this.handleSessionGone(origin);
  };

  /**
   * Tear down local session state synchronously; the returned promise settles when the server-side
   * Leave has LANDED (never rejects). Await it when the next action requires the membership to be
   * gone server-side — e.g. the Forms tab suspends before a backend screen regen, whose collab
   * guard checks for live participants. Fire-and-forget callers may ignore the result unchanged.
   */
  public leave = (): Promise<void> => {
    // Any join still in flight belongs to a superseded generation — abandon it.
    this.joinEpoch += 1;
    const entityId: string | null = this.sessionEntityId;
    const token: string | null = this.leaveToken;
    this.sessionEntityId = null;
    this.leaveToken = null;
    this.lastSeq = 0;
    this.shadowDoc = null;
    this.outgoingOps = [];
    this.inFlightOps = [];
    this.undoEntries = [];
    this.redoEntries = [];
    this.ackedKeySeq.clear();
    this.optimisticIndexByOp.clear();
    this.lastStructuralSeq = 0;

    if (this.opFlushTimer) {
      clearTimeout(this.opFlushTimer);
      this.opFlushTimer = null;
    }
    if (this.selectionTimer) {
      clearTimeout(this.selectionTimer);
      this.selectionTimer = null;
    }
    if (this.streamHandle) {
      this.streamHandle.stop();
      this.streamHandle = null;
    }
    if (this.pagehideHandler) {
      window.removeEventListener("pagehide", this.pagehideHandler);
      this.pagehideHandler = null;
    }

    this.host.onSessionLeft();
    if (entityId && token) {
      // Promise.resolve-wrapped: tolerate a void-returning stub (tests) exactly like a real call.
      return Promise.resolve(CollabService.Leave(entityId, COLLAB_CLIENT_ID, token, this.entityRoute)).then(
        (): void => undefined,
        (): void => undefined
      );
    }
    return Promise.resolve();
  };

  // ---- local mutations → ops ---------------------------------------------------------------

  public emitLocalOps = (): void => {
    if (!this.shadowDoc || this.resyncInFlight) return;
    try {
      const liveDoc: TDoc | null = this.adapter.getSerializableDoc();
      if (!liveDoc) return;
      if (this.adapter.getDocId) {
        // Identity guard (KOA-18173): during an entity switch the live doc can already be
        // the NEW entity while the session/shadow still belong to the OLD one. Diffing
        // across identities fabricates ops that turn one entity into the other (and both
        // trees share the special/template node ids, so the result is a destructive
        // merge). Skip — never resync here, the switch is about to leave this session.
        const liveId: string | null = this.adapter.getDocId(liveDoc);
        const shadowId: string | null = this.adapter.getDocId(this.shadowDoc);
        const sessionId: string | null = this.sessionEntityId;
        const liveVsSession: boolean = liveId != null && sessionId != null && !collabIdsEqual(liveId, sessionId);
        const shadowVsLive: boolean = liveId != null && shadowId != null && !collabIdsEqual(shadowId, liveId);
        if (liveVsSession || shadowVsLive) {
          return;
        }
      }

      const ops: TOp[] = this.adapter.diff(this.shadowDoc, liveDoc);
      if (ops.length === 0) return;
      // Per-user undo: capture the gesture's inverse against the PRE-mutation shadow before
      // advancing it. Any new gesture invalidates the redo branch, like every undo model.
      const inverseOps: TOp[] = this.adapter.invert(this.shadowDoc, ops);
      this.undoEntries.push({ forwardOps: ops, inverseOps });
      if (this.undoEntries.length > COLLAB_UNDO_STACK_LIMIT) this.undoEntries.shift();
      this.redoEntries = [];

      // Optimistic placement capture (KOA-18198): replay the gesture op-by-op onto the
      // pre-advance shadow — mirroring how the server resolves each op against the tree
      // state mid-batch — recording where each structural op lands. processAckResults
      // compares the server's resolvedIndex against this to drop agreeing corrections.
      // The mutated shadow is discarded on the next line (advanced to a clone of live).
      this.captureOptimisticIndices(this.shadowDoc, ops);

      this.shadowDoc = cloneDoc(liveDoc);
      this.outgoingOps.push(...ops);
      this.scheduleOpFlush();
    } catch (error: unknown) {
      console.error("[collab] diffing the local tree failed:", error);
      // A diff failure means the shadow can no longer be trusted — resync from authority.
      void this.resyncFromAuthority();
    }
  };

  private scheduleOpFlush = (delayMs: number = OP_FLUSH_THROTTLE_MS): void => {
    if (this.opFlushTimer) return;
    this.opFlushTimer = setTimeout(() => {
      this.opFlushTimer = null;
      void this.flushOps();
    }, delayMs);
  };

  private sendRetryDelayMs = (): number => {
    return Math.min(OP_FLUSH_THROTTLE_MS * 2 ** this.consecutiveSendFailures, OP_FLUSH_BACKOFF_MAX_MS);
  };

  public flushOps = async (): Promise<void> => {
    if (!this.sessionEntityId || this.opSendInFlight) {
      if (this.outgoingOps.length > 0) this.scheduleOpFlush();
      return;
    }
    if (this.outgoingOps.length === 0) return;

    const coalesced: TOp[] = this.adapter.coalesce(this.outgoingOps);
    // Bounded slice under the server's batch cap; the tail stays queued AHEAD of
    // anything the user produces during the request, preserving op order.
    const batch: TOp[] = coalesced.slice(0, COLLAB_MAX_OPS_PER_BATCH);
    this.outgoingOps = coalesced.slice(COLLAB_MAX_OPS_PER_BATCH);
    this.inFlightOps = batch;
    this.opSendInFlight = true;

    try {
      const res: AxiosResponse<ICollabOpsResponse> = await CollabService.SendOps(
        this.sessionEntityId,
        COLLAB_CLIENT_ID,
        batch,
        this.entityRoute
      );
      if (!res || res.status !== 200 || !res.data) {
        // Transport-level failure: keep the batch for an idempotent retry (opIds dedup).
        this.outgoingOps = [...batch, ...this.outgoingOps];
        this.consecutiveSendFailures += 1;
        this.scheduleOpFlush(this.sendRetryDelayMs());
        return;
      }

      this.consecutiveSendFailures = 0;
      this.lastSeq = Math.max(this.lastSeq, res.data.seq);
      const rejected: ICollabOpResult[] = res.data.results.filter((r: ICollabOpResult) => r.status === "rejected");
      const remaps: ICollabOpResult[] = res.data.results.filter(
        (r: ICollabOpResult) => r.idRemap && Object.keys(r.idRemap).length > 0
      );

      for (const remap of remaps) {
        this.applyIdRemapLocally(remap.idRemap ?? {});
      }
      this.processAckResults(batch, res.data.results);
      // Server-origin derived ops (workflow wfLaneDerived): the hub suppresses our own echo,
      // so the ORIGIN client must apply them from the ack. Non-origin clients get them
      // inside the broadcast ops batch instead.
      const syntheticOps: TOp[] = (res.data.syntheticOps ?? []) as TOp[];
      if (syntheticOps.length > 0) {
        this.applyServerSyntheticOps(syntheticOps);
      }
      if (rejected.length > 0) {
        // A reject means our optimistic local state diverged from the authority (target
        // vanished, cycle, …). No local repair heuristics — converge from the source.
        await this.resyncFromAuthority();
      }
    } catch (error: unknown) {
      const status: number | undefined = (error as { response?: { status?: number } })?.response?.status;
      // 401 belongs to the auth interceptor (token refresh), 408/429 are retryable by
      // definition; every other 4xx is a deterministic server verdict on THIS payload
      // (BAD_BATCH, NOT_PARTICIPANT, session gone) — retrying the identical batch can
      // never succeed and used to loop forever at the flush cadence.
      const isTerminalReject: boolean =
        status !== undefined && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
      if (CollabSessionCore.isSessionGoneStatus(status)) {
        // Not a verdict on the payload — the session (or our membership in it) is gone.
        // Resyncing cannot fix that: GET /state 404s too, and the swallowed failure is
        // what left the tab silently discarding every later edit (KOA-18256).
        this.consecutiveSendFailures = 0;
        await this.handleSessionGone("sendOps");
      } else if (isTerminalReject) {
        this.consecutiveSendFailures = 0;
        await this.resyncFromAuthority();
      } else {
        this.outgoingOps = [...batch, ...this.outgoingOps];
        this.consecutiveSendFailures += 1;
        this.scheduleOpFlush(this.sendRetryDelayMs());
      }
    } finally {
      this.inFlightOps = [];
      this.opSendInFlight = false;
      if (this.outgoingOps.length > 0) this.scheduleOpFlush();
    }
  };

  // ---- per-user undo/redo (multi-participant sessions) ------------------------------------

  public canUndo = (): boolean => this.undoEntries.length > 0;

  public canRedo = (): boolean => this.redoEntries.length > 0;

  public undo = (): void => {
    const entry: ICollabUndoEntry<TOp> | undefined = this.undoEntries.pop();
    if (!entry) return;
    this.redoEntries.push(entry);
    this.applyLocalOpBatch(entry.inverseOps);
  };

  public redo = (): void => {
    const entry: ICollabUndoEntry<TOp> | undefined = this.redoEntries.pop();
    if (!entry) return;
    this.undoEntries.push(entry);
    this.applyLocalOpBatch(entry.forwardOps);
  };

  /**
   * Applies an undo/redo batch as if it were a fresh local gesture: fresh opIds (the server
   * dedups by opId — replaying consumed ids would be silently ignored), no resolvedIndex
   * (anchor-relative positions re-resolve against the CURRENT sibling state), applied to
   * both docs and queued through the normal send pipeline.
   */
  private applyLocalOpBatch = (ops: TOp[]): void => {
    const reminted: TOp[] = ops.map((op: TOp): TOp => this.adapter.remintForReplay(op));

    // Same per-op optimistic placement capture as emitLocalOps — here the sequential
    // application onto the shadow is also its real advance (no clone follows).
    const shadowSkipped: TOp[] = this.shadowDoc ? this.captureOptimisticIndices(this.shadowDoc, reminted) : [];
    const liveDoc: TDoc = this.adapter.getLiveDoc();
    const liveSkipped: TOp[] = this.adapter.applyOps(liveDoc, reminted);
    this.dispatchRemoteChange(liveDoc, "op");

    this.outgoingOps.push(...reminted);
    this.scheduleOpFlush();

    // KOA-18191 hardening: an undo/redo replay whose ops SKIP locally used to be
    // swallowed — the server may still accept them, which is silent divergence in the
    // making. Surface every skip; converge when the domain says a skip is fatal.
    const skipped: TOp[] = liveSkipped.length > 0 ? liveSkipped : shadowSkipped;
    if (skipped.length > 0) {
      if (skipped.some((op: TOp): boolean => this.adapter.skippedOpForcesResync(op))) {
        void this.resyncFromAuthority();
      }
    }
  };

  /**
   * Applies `ops` onto `doc` one at a time, recording each structural op's optimistic
   * node placement (adapter.optimisticIndexOf) after its own application — the same
   * per-op tree state the server resolves against. No-op recording (single batch apply)
   * when the adapter does not implement the hook. Returns the ops the apply skipped,
   * so callers can surface silent divergence (KOA-18191) — same contract as applyOps.
   */
  private captureOptimisticIndices = (doc: TDoc, ops: TOp[]): TOp[] => {
    if (!this.adapter.optimisticIndexOf) {
      return this.adapter.applyOps(doc, ops);
    }
    const skipped: TOp[] = [];
    for (const op of ops) {
      skipped.push(...this.adapter.applyOps(doc, [op]));
      if (this.adapter.isStructural(op)) {
        const index: number | null = this.adapter.optimisticIndexOf(doc, op);
        if (index !== null) {
          this.optimisticIndexByOp.set(op, index);
        }
      }
    }
    return skipped;
  };

  /**
   * Ack handling (mirrors the convergence-simulation semantics exactly): record per-key
   * acked seqs for the LWW tiebreaker, correct our own structural positions to the
   * server-resolved index, and adopt server stamps the origin cannot receive any other way
   * (workflow remove cascades — the hub suppresses the origin's broadcast echo). A stale
   * correction (a higher-seq remote structural op already applied here) triggers a resync
   * instead of guessing.
   */
  private processAckResults = (batch: TOp[], results: ICollabOpResult[]): void => {
    let correctionApplied: boolean = false;
    let staleCorrection: boolean = false;
    const adoptionOps: TOp[] = [];

    for (let i = 0; i < results.length && i < batch.length; i++) {
      const result: ICollabOpResult = results[i];
      const op: TOp = batch[i];
      // Consume the op's optimistic placement whatever the outcome — a rejected op's
      // entry must not linger (the map is keyed by op object identity).
      const optimisticIndex: number | undefined = this.optimisticIndexByOp.get(op);
      this.optimisticIndexByOp.delete(op);
      if (result.status !== "accepted") continue;

      const key: string | null = this.adapter.lwwKeyOf(op);
      if (key) {
        this.ackedKeySeq.set(key, Math.max(this.ackedKeySeq.get(key) ?? -1, result.seq));
      }

      if (this.adapter.isStructural(op)) {
        if (result.seq < this.lastStructuralSeq) {
          staleCorrection = true;
          continue;
        }
        this.lastStructuralSeq = result.seq;
      }

      if (this.adapter.makeAckAdoptionOps) {
        adoptionOps.push(...this.adapter.makeAckAdoptionOps(op, result));
      }

      if (result.resolvedIndex !== undefined && result.resolvedIndex !== null) {
        if (optimisticIndex === result.resolvedIndex) {
          // The server placed the node exactly where this client's optimistic apply
          // already did — nothing to heal. Applying the correction anyway would rebase
          // a stale position onto a tree that LATER local gestures may have changed
          // (KOA-18198 flicker) and force a redundant re-render.
          continue;
        }
        const correction: TOp | null = this.adapter.makeResolvedIndexCorrection(op, result.resolvedIndex);
        if (!correction) continue;
        if (this.shadowDoc) this.adapter.applyOps(this.shadowDoc, [correction]);
        const liveDoc: TDoc = this.adapter.getLiveDoc();
        this.adapter.applyOps(liveDoc, [correction]);
        correctionApplied = true;
      }
    }

    // Server-stamp adoption (workflow: the ORIGIN applies the authority's remove cascade
    // from its ack). Idempotent by remove semantics — already-gone targets are accepted
    // no-ops — and skipped when a resync is already due (the snapshot supersedes it).
    if (adoptionOps.length > 0 && !staleCorrection) {
      if (this.shadowDoc) this.adapter.applyOps(this.shadowDoc, adoptionOps);
      const liveDoc: TDoc = this.adapter.getLiveDoc();
      this.adapter.applyOps(liveDoc, adoptionOps);
      correctionApplied = true;
    }

    if (correctionApplied) {
      this.dispatchRemoteChange(this.adapter.getLiveDoc(), "op");
    }
    if (staleCorrection) {
      void this.resyncFromAuthority();
    }
  };

  /**
   * Applies server-origin derived ops from an ops ack onto both docs. No LWW/structural
   * bookkeeping: these ops are server-authored (never client-pending on the same key) and
   * their seqs are already covered by the ack's `seq` watermark.
   */
  private applyServerSyntheticOps = (ops: TOp[]): void => {
    if (this.shadowDoc) this.adapter.applyOps(this.shadowDoc, ops);
    const liveDoc: TDoc = this.adapter.getLiveDoc();
    this.adapter.applyOps(liveDoc, ops);
    this.dispatchRemoteChange(liveDoc, "op");
  };

  /** Server remapped colliding ids inside one of our adds — re-key the local docs. */
  private applyIdRemapLocally = (idRemap: ICollabIdRemap): void => {
    const liveDoc: TDoc = this.adapter.getLiveDoc();
    this.adapter.remapIds(liveDoc, idRemap);
    if (this.shadowDoc) this.adapter.remapIds(this.shadowDoc, idRemap);
    this.dispatchRemoteChange(liveDoc, "op");
  };

  // ---- remote ops → local doc ----------------------------------------------------------------

  private handleStreamEvent = (streamEvent: ICollabStreamEvent): void => {
    switch (streamEvent.type) {
      case "roster":
      case "join":
      case "leave": {
        this.host.applyRosterUpdate(streamEvent.roster ?? []);
        break;
      }
      case "selection": {
        const updated: ICollabParticipant | undefined = streamEvent.participant;
        if (updated) {
          const roster: ICollabParticipant[] = this.host
            .getRoster()
            .map((p: ICollabParticipant) => (p.clientId === updated.clientId ? updated : p));
          this.host.applyRosterUpdate(roster);
        }
        break;
      }
      case "ops": {
        this.handleRemoteOps(streamEvent);
        break;
      }
      case "tree": {
        if (streamEvent.json && streamEvent.seq > this.lastSeq) {
          this.lastSeq = streamEvent.seq;
          this.applyAuthoritativeSnapshot(streamEvent.json);
        }
        break;
      }
      default:
        break;
    }
  };

  private handleRemoteOps = (streamEvent: ICollabStreamEvent): void => {
    if (this.resyncInFlight) return;
    if (streamEvent.clientId === COLLAB_CLIENT_ID) return; // echo (hub suppresses; defensive)
    const ops: TOp[] = (streamEvent.ops ?? []) as TOp[];
    if (ops.length === 0) return;

    const firstSeq: number = streamEvent.firstSeq ?? streamEvent.seq - ops.length + 1;
    if (firstSeq > this.lastSeq + 1) {
      void this.replayGap();
      return;
    }
    if (streamEvent.seq <= this.lastSeq) return; // stale duplicate

    this.lastSeq = streamEvent.seq;
    this.applyRemoteOpsToDocs(ops, firstSeq);
  };

  private applyRemoteOpsToDocs = (ops: TOp[], firstSeq: number): void => {
    // Mirrors the convergence-simulation delivery rules exactly:
    // - the adapter's optional conservative conflict rule fires first (workflow:
    //   element-scope upsert conflicts, structural-while-structural-pending → resync);
    // - structural ops must respect the global seq order (violation → resync, no guessing);
    // - prop/screen ops are suppressed when we hold an in-flight write on a CONFLICTING key
    //   (ours sequences later and wins) or when a conflicting ACKED write is newer
    //   (seq-aware LWW — a late-delivered older broadcast must not clobber it). Key
    //   conflict is exact equality unless the adapter provides `lwwKeysConflict`
    //   (workflow: ancestor/descendant prop-path prefixes).
    const pendingOps: TOp[] = [...this.outgoingOps, ...this.inFlightOps];
    const pendingKeys: Set<string> = new Set<string>();
    for (const op of pendingOps) {
      if (this.adapter.isStructural(op)) continue;
      const key: string | null = this.adapter.lwwKeyOf(op);
      if (key) pendingKeys.add(key);
    }

    const applicable: TOp[] = [];
    for (let index = 0; index < ops.length; index++) {
      const op: TOp = ops[index];
      const remoteSeq: number = firstSeq + index;

      if (
        this.adapter.remoteOpForcesResync &&
        this.adapter.remoteOpForcesResync(op, remoteSeq, pendingOps, this.ackedKeySeq)
      ) {
        void this.resyncFromAuthority();
        return;
      }

      if (this.adapter.isStructural(op)) {
        if (remoteSeq < this.lastStructuralSeq) {
          void this.resyncFromAuthority();
          return;
        }
        this.lastStructuralSeq = remoteSeq;
        applicable.push(op);
        continue;
      }

      const key: string | null = this.adapter.lwwKeyOf(op);
      if (key) {
        if (this.hasConflictingPendingKey(pendingKeys, key)) continue;
        if (this.hasNewerConflictingAckedKey(key, remoteSeq)) continue;
      }
      applicable.push(op);
    }

    if (this.shadowDoc) this.adapter.applyOps(this.shadowDoc, applicable);
    if (applicable.length === 0) return;

    const liveDoc: TDoc = this.adapter.getLiveDoc();
    const skippedOps: TOp[] = this.adapter.applyOps(liveDoc, applicable);
    this.dispatchRemoteChange(liveDoc, "op");

    // A skipped remote op the adapter flags (screen: a nodeAdd whose id collides with one of
    // our optimistic local nodes — both tabs minted the same id inside the in-flight window,
    // ~impossible odds) leaves this replica divergent; the deterministic recovery is a resync
    // once our own ack lands with the server's remap.
    if (skippedOps.some((op: TOp) => this.adapter.skippedOpForcesResync(op))) {
      void this.resyncFromAuthority();
    }
  };

  /** In-flight suppression key match: exact by default, adapter-conflict-aware when provided. */
  private hasConflictingPendingKey = (pendingKeys: ReadonlySet<string>, key: string): boolean => {
    const conflict: ((a: string, b: string) => boolean) | undefined = this.adapter.lwwKeysConflict;
    if (!conflict) return pendingKeys.has(key);
    for (const pendingKey of pendingKeys) {
      if (conflict(pendingKey, key)) return true;
    }
    return false;
  };

  /**
   * Acked-seq suppression: exact-key lookup by default; with an adapter conflict rule the
   * remote op must out-sequence EVERY conflicting acked key (a prefix-conflicting
   * comparison is between different keys — suppression is the sound conservative outcome).
   */
  private hasNewerConflictingAckedKey = (key: string, remoteSeq: number): boolean => {
    const conflict: ((a: string, b: string) => boolean) | undefined = this.adapter.lwwKeysConflict;
    if (!conflict) return (this.ackedKeySeq.get(key) ?? -1) >= remoteSeq;
    for (const [ackedKey, ackedSeq] of this.ackedKeySeq) {
      if (ackedSeq >= remoteSeq && conflict(ackedKey, key)) return true;
    }
    return false;
  };

  private dispatchRemoteChange = (doc: TDoc, kind: CollabRemoteChangeKind): void => {
    this.applyingRemoteChange = true;
    try {
      this.adapter.commitRemoteDoc(doc, kind);
    } finally {
      this.applyingRemoteChange = false;
    }
  };

  // ---- resync -------------------------------------------------------------------------------------

  private replayGap = async (): Promise<void> => {
    if (!this.sessionEntityId || this.resyncInFlight) return;
    try {
      const res: AxiosResponse<ICollabReplayResponse> = await CollabService.Replay(
        this.sessionEntityId,
        this.lastSeq,
        this.entityRoute
      );
      if (res && res.status === 200 && res.data) {
        for (const payload of res.data.events) {
          const parsed: unknown = JSON.parse(payload);
          this.handleStreamEvent(parsed as ICollabStreamEvent);
        }
        return;
      }
      await this.resyncFromAuthority();
    } catch {
      await this.resyncFromAuthority();
    }
  };

  private resyncFromAuthority = async (): Promise<void> => {
    if (!this.sessionEntityId || this.resyncInFlight) return;
    this.resyncInFlight = true;
    try {
      const res: AxiosResponse<ICollabStateResponse> = await CollabService.GetState(
        this.sessionEntityId,
        this.entityRoute
      );
      if (!res || res.status !== 200 || !res.data) return;
      this.host.applyRosterUpdate(res.data.roster ?? []);
      if (res.data.snapshotJson) {
        this.lastSeq = res.data.seq;
        this.applyAuthoritativeSnapshot(res.data.snapshotJson);
      }
      // Unsent local edits survive a resync: their targets may have changed, but opIds make
      // retries idempotent and any reject falls back here again.
      if (this.outgoingOps.length > 0) this.scheduleOpFlush();
    } catch (error: unknown) {
      const status: number | undefined = (error as { response?: { status?: number } })?.response?.status;
      if (CollabSessionCore.isSessionGoneStatus(status)) {
        // The authority we were resyncing from does not exist any more. Swallowing this
        // (the old behavior) is what made the dead session permanent.
        this.resyncInFlight = false;
        await this.handleSessionGone("resync");
        return;
      }
    } finally {
      this.resyncInFlight = false;
    }
  };

  private recoverAfterReconnect = (): void => {
    void this.resyncFromAuthority();
  };

  /**
   * Adapter/host-requested convergence — e.g. a remote workflow op the domain cannot
   * materialize granularly onto the live canvas. Full resync beats silent divergence.
   */
  public requestResyncFromAuthority = (): void => {
    void this.resyncFromAuthority();
  };

  /**
   * Re-seeds the shadow from the current live doc. For domains whose authoritative resync
   * commits ASYNCHRONOUSLY (workflow: importXML of the envelope finishes after
   * commitRemoteDoc returns) — once the live doc reflects the authority, the host calls
   * this so "shadow == live minus unsent local edits" holds again.
   */
  public reseedShadowFromLive = (): void => {
    const liveDoc: TDoc | null = this.adapter.getSerializableDoc();
    if (liveDoc) {
      this.shadowDoc = cloneDoc(liveDoc);
    }
  };

  private applyAuthoritativeSnapshot = (json: string): void => {
    try {
      const doc: TDoc | null = this.adapter.parseSnapshot(json);
      if (!doc) return;
      this.shadowDoc = cloneDoc(doc);
      // A wholesale authoritative replace invalidates gesture inverses computed against the
      // pre-resync world — conservative reset beats undoing into a diverged state. The
      // LWW/order bookkeeping restarts with the snapshot too.
      this.undoEntries = [];
      this.redoEntries = [];
      this.ackedKeySeq.clear();
      this.optimisticIndexByOp.clear();
      this.lastStructuralSeq = 0;
      this.dispatchRemoteChange(doc, "resync");
    } catch (error: unknown) {
      console.error("[collab] applying the authoritative snapshot failed:", error);
    }
  };

  // ---- presence -----------------------------------------------------------------------------------

  public scheduleSelectionBroadcast = (): void => {
    if (this.selectionTimer) return;
    this.selectionTimer = setTimeout(() => {
      this.selectionTimer = null;
      void this.broadcastSelection();
    }, SELECTION_THROTTLE_MS);
  };

  /** Live cursor from the canvas iframe (already 80ms-throttled agent-side). */
  public onLocalCursorMoved = (x: number, y: number): void => {
    if (!this.sessionEntityId) return;
    this.localCursor = { x, y };
    this.scheduleSelectionBroadcast();
  };

  private broadcastSelection = async (): Promise<void> => {
    if (!this.sessionEntityId) return;
    try {
      const selectedIds: string[] = this.host.getSelectedIds();
      await CollabService.Presence(
        this.sessionEntityId,
        COLLAB_CLIENT_ID,
        selectedIds,
        this.localCursor,
        this.entityRoute
      );
    } catch {
      // Presence is best-effort — never surface an error for it.
    }
  };
}
